import type { SchoolQuizOptionGroup } from "@/client"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Badge, Box, Button, HStack, Stack, Text } from "@chakra-ui/react"
import { useState } from "react"

interface SchoolQuizFilterProps {
  groups: SchoolQuizOptionGroup[]
  selectedQuizIds: string[]
  onChange: (quizIds: string[]) => void
  isLoading?: boolean
}

export function SchoolQuizFilter({
  groups,
  selectedQuizIds,
  onChange,
  isLoading,
}: SchoolQuizFilterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const selected = new Set(selectedQuizIds)
  const triggerLabel =
    selectedQuizIds.length === 0
      ? "All quizzes"
      : `${selectedQuizIds.length} quizzes`

  function toggleQuiz(quizId: string) {
    const next = new Set(selected)
    if (next.has(quizId)) {
      next.delete(quizId)
    } else {
      next.add(quizId)
    }
    onChange(Array.from(next))
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={({ open }) => setIsOpen(open)}>
      <DialogTrigger asChild>
        <Button variant="outline" loading={isLoading} aria-label={triggerLabel}>
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select quizzes</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            {groups.map((group) => (
              <Box key={group.group_key}>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="semibold">{group.group_label}</Text>
                  <Badge variant="subtle">{group.quizzes.length}</Badge>
                </HStack>
                <Stack gap={2}>
                  {group.quizzes.map((quiz) => (
                    <Box
                      key={`${group.group_key}-${quiz.quiz_id}`}
                      borderWidth="1px"
                      borderRadius="md"
                      px={3}
                      py={2}
                    >
                      <Checkbox
                        checked={selected.has(quiz.quiz_id)}
                        onCheckedChange={() => toggleQuiz(quiz.quiz_id)}
                      >
                        <Stack gap={0}>
                          <HStack>
                            <Text>{quiz.quiz_name}</Text>
                            {quiz.is_practice ? (
                              <Badge colorPalette="orange">Practice</Badge>
                            ) : null}
                          </HStack>
                          <Text color="fg.muted" fontSize="sm">
                            {quiz.completed_attempts}/{quiz.started_attempts}{" "}
                            completed
                          </Text>
                        </Stack>
                      </Checkbox>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  )
}
